import { MultiLocationAnalyticsService } from '../../core/multi_location_analytics_sdk/multi-location-analytics.service';
import { Observable, from } from 'rxjs';
import {
  Aggregate,
  AlignmentPeriod,
  DateRange,
  Dimension,
  GroupBy,
  Measure,
  MetricResult,
  OrderBy,
  QueryMetricsRequest,
  QueryMetricsResponse,
} from '../../core/multi_location_analytics_sdk/_internal/objects';
import {
  Alignment,
  Calendar,
  CompositeFilter_Operator,
  FieldFilter_Operator,
  Operator,
  Order,
  Type,
} from '../../core/multi_location_analytics_sdk/_internal/enums';
import { filter, map, shareReplay } from 'rxjs/operators';
import { Ga4RecordMap, Ga4SourceMap, Ga4GoalMap } from './ga4-metrics.interface';
import { Injectable } from '@angular/core';
import { COLLECTION_DAYS_THRESHOLD } from './ga4-metrics-config';
import { alignLocalDateToUTC, alignUTC } from '../../core/metrics.helper';
import { GaGoalData } from '@vendasta/uikit';
import { Category } from '@vendasta/google-analytics';
import { ChartEntry } from '../metrics';
import { differenceInDays, format } from 'date-fns';
import { AnalyticsJSON, GA4AnalyticsGraphDefinition } from '../analytics/analytics.interfaces';
import { HttpClient } from '@angular/common/http';

declare const gapi: { client: any };

@Injectable({
  providedIn: 'root',
})
export class Ga4MetricsService {
  legacyDefaultPropertyId = '';
  siteId: string;
  respdata: Observable<any>;
  retdata: Ga4RecordMap;
  lineGraphDefinitionsTotal: GA4AnalyticsGraphDefinition = {
    dimension: [
      {
        name: '',
      },
    ],
    metrics: [
      {
        name: 'activeUsers',
      },
      {
        name: 'sessions',
      },
      {
        name: 'averageSessionDuration',
      },
      {
        name: 'screenPageViews',
      },
      {
        name: 'screenPageViewsPerSession',
      },
      {
        name: 'newUsers',
      },
      {
        name: 'bounceRate',
      },
      {
        name: 'engagedSessions',
      },
    ],
  };
  lineGraphDefinitions: GA4AnalyticsGraphDefinition = {
    dimension: [
      {
        name: 'date',
      },
    ],
    metrics: [
      {
        name: 'activeUsers',
      },
      {
        name: 'sessions',
      },
      {
        name: 'averageSessionDuration',
      },
      {
        name: 'screenPageViews',
      },
      {
        name: 'screenPageViewsPerSession',
      },
      {
        name: 'newUsers',
      },
      {
        name: 'bounceRate',
      },
      {
        name: 'engagedSessions',
      },
    ],
  };
  topMetricsDefinitions: GA4AnalyticsGraphDefinition = {
    dimension: [
      {
        name: 'sessionSource',
      },
    ],
    metrics: [
      {
        name: 'sessions',
      },
    ],
  };
  treafficMetricsDefinitions: GA4AnalyticsGraphDefinition = {
    dimension: [
      {
        name: 'sessionDefaultChannelGrouping',
      },
    ],
    metrics: [
      {
        name: 'sessions',
      },
    ],
  };

  data: Ga4RecordMap;

  constructor(private multiLocationAnalyticsService: MultiLocationAnalyticsService, private http: HttpClient) {}
  getGa4RecordstTotal(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    siteId: string,
    defaultTracking: boolean,
    legacyDefaultPropertyId: string,
    accessToken: string,
  ): Observable<Ga4RecordMap> {
    if (defaultTracking === true) {
      const recordDataForActiveUser: ChartEntry[] = [];
      const recordDataForSessions: ChartEntry[] = [];
      const recordDataForSessionDuration: ChartEntry[] = [];
      const recordDataForPageViews: ChartEntry[] = [];
      const recordDataForPageViewsPerSession: ChartEntry[] = [];
      const recordDataForNewUsers: ChartEntry[] = [];
      const recordDataForBounceRate: ChartEntry[] = [];
      const recordDataForEngSession: ChartEntry[] = [];
      return new Observable<Ga4RecordMap>((observer) => {
        gapi.client
          .init({
            discoveryDocs: ['https://analyticsdata.googleapis.com/$discovery/rest?version=v1beta'],
          })
          .then(() => {
            gapi.client.setToken({
              access_token: accessToken,
            });
            const respdata = this.buildQuery(
              this.getQueryOptions(this.lineGraphDefinitionsTotal, startDate, endDate, siteId, true),
            );
            respdata.subscribe((response) => {
              if (typeof response.result.rows === 'undefined') {
                observer.next({});
                observer.complete();
                return {};
              }
              const metrics = response.result.rows;
              if (!metrics) {
                return {};
              }

              metrics.map((m) => {
                // The order aligns with the measurement order in the request
                recordDataForActiveUser.push({
                  name: 'activeUsers',
                  value: Number(m.metricValues[0].value),
                } as ChartEntry);

                recordDataForSessions.push({
                  name: 'sessions',
                  value: Number(m.metricValues[1].value),
                } as ChartEntry);
                recordDataForSessionDuration.push({
                  name: 'sessionDuration',
                  value: Number(m.metricValues[2].value),
                } as ChartEntry);
                recordDataForPageViews.push({
                  name: 'pageViews',
                  value: Number(m.metricValues[3].value),
                } as ChartEntry);
                recordDataForPageViewsPerSession.push({
                  name: 'pageViewsPerSession',
                  value: Number(m.metricValues[4].value),
                } as ChartEntry);
                recordDataForNewUsers.push({
                  name: 'newUsers',
                  value: Number(m.metricValues[5].value),
                } as ChartEntry);
                recordDataForBounceRate.push({
                  name: 'bounceRate',
                  value: Number(m.metricValues[6].value) * 100,
                } as ChartEntry);
                recordDataForEngSession.push({
                  name: 'engagedSessions',
                  value: Number(m.metricValues[7].value),
                } as ChartEntry);
              });
              const data = {
                activeUsers: recordDataForActiveUser,
                sessions: recordDataForSessions,
                sessionDuration: recordDataForSessionDuration,
                pageViews: recordDataForPageViews,
                pageViewsPerSession: recordDataForPageViewsPerSession,
                newUsers: recordDataForNewUsers,
                bounceRate: recordDataForBounceRate,
                engagedSessions: recordDataForEngSession,
              } as Ga4RecordMap;
              observer.next(data);
              observer.complete();
            });
          });
      });
    } else {
      return new Observable<Ga4RecordMap>((observer) => {
        observer.next({});
        observer.complete();
      });
    }
  }
  getGa4Records(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    siteId: string,
    defaultTracking: boolean,
    legacyDefaultPropertyId: string,
    accessToken: string,
  ): Observable<Ga4RecordMap> {
    this.legacyDefaultPropertyId = legacyDefaultPropertyId;
    if (defaultTracking === false) {
      const req: QueryMetricsRequest = this.getBaseQuery('google_analytics_GA4_record', partnerId, businessId);
      // The order of listing each measure in the request matters - the results will be in a list and each of them is in the same order as that in the request
      req.measures = [
        new Measure({
          aggregate: new Aggregate({
            measure: 'active_users',
            aggOp: Operator.SUM,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'sessions',
            aggOp: Operator.SUM,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'session_duration',
            aggOp: Operator.SUM,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'bounce_rate',
            aggOp: Operator.AVG,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'page_views',
            aggOp: Operator.SUM,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'page_views_per_session',
            aggOp: Operator.AVG,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'new_users',
            aggOp: Operator.SUM,
          }),
        }),
        new Measure({
          aggregate: new Aggregate({
            measure: 'engaged_sessions',
            aggOp: Operator.SUM,
          }),
        }),
      ];
      req.groupBy = new GroupBy({
        dimension: [
          new Dimension({
            dimension: 'collection_timestamp',
          }),
        ],
      });
      req.alignmentPeriod = new AlignmentPeriod({
        calendar:
          differenceInDays(endDate, startDate) >= COLLECTION_DAYS_THRESHOLD
            ? Calendar.CALENDAR_MONTH
            : Calendar.CALENDAR_DAY,
      });
      req.alignment = Alignment.ALIGN_DELTA;
      req.dateRange = new DateRange({
        start: startDate,
        end: endDate,
      });
      return this.multiLocationAnalyticsService.queryMetrics(req).pipe(
        filter((resp) => resp != null),
        map((resp: QueryMetricsResponse) => {
          const metrics = resp.metricResults[0].metrics.metrics;
          if (!metrics) {
            return {};
          }
          const recordDataForActiveUser: ChartEntry[] = [];
          const recordDataForSessions: ChartEntry[] = [];
          const recordDataForSessionDuration: ChartEntry[] = [];
          const recordDataForPageViews: ChartEntry[] = [];
          const recordDataForPageViewsPerSession: ChartEntry[] = [];
          const recordDataForNewUsers: ChartEntry[] = [];
          const recordDataForBounceRate: ChartEntry[] = [];
          const recordDataForEngSession: ChartEntry[] = [];

          metrics.map((m) => {
            // The order aligns with the measurement order in the request
            recordDataForActiveUser.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[0],
            } as ChartEntry);
            recordDataForSessions.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[1],
            } as ChartEntry);
            recordDataForSessionDuration.push({
              name: alignLocalDateToUTC(m.dimension),
              value: Number(m.measures[2]) * Number(m.measures[1]),
            } as ChartEntry);
            recordDataForBounceRate.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[3],
            } as ChartEntry);
            recordDataForPageViews.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[4],
            } as ChartEntry);
            recordDataForPageViewsPerSession.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[5],
            } as ChartEntry);
            recordDataForNewUsers.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[6],
            } as ChartEntry);
            recordDataForEngSession.push({
              name: alignLocalDateToUTC(m.dimension),
              value: m.measures[7],
            } as ChartEntry);
          });
          return {
            activeUsers: recordDataForActiveUser,
            sessions: recordDataForSessions,
            pageViews: recordDataForPageViews,
            sessionDuration: recordDataForSessionDuration,
            pageViewsPerSession: recordDataForPageViewsPerSession,
            newUsers: recordDataForNewUsers,
            bounceRate: recordDataForBounceRate,
            engagedSessions: recordDataForEngSession,
          } as Ga4RecordMap;
        }),
        shareReplay(1),
      );
    } else {
      const recordDataForActiveUser: ChartEntry[] = [];
      const recordDataForSessions: ChartEntry[] = [];
      const recordDataForSessionDuration: ChartEntry[] = [];
      const recordDataForPageViews: ChartEntry[] = [];
      const recordDataForPageViewsPerSession: ChartEntry[] = [];
      const recordDataForNewUsers: ChartEntry[] = [];
      const recordDataForBounceRate: ChartEntry[] = [];
      const recordDataForEngSession: ChartEntry[] = [];
      return new Observable<Ga4RecordMap>((observer) => {
        gapi.client
          .init({
            discoveryDocs: ['https://analyticsdata.googleapis.com/$discovery/rest?version=v1beta'],
          })
          .then(() => {
            gapi.client.setToken({
              access_token: accessToken,
            });
            const respdata = this.buildQuery(
              this.getQueryOptions(this.lineGraphDefinitions, startDate, endDate, siteId, false),
            );
            respdata.subscribe((response) => {
              if (typeof response.result.rows === 'undefined') {
                observer.next({});
                observer.complete();
                return {};
              }
              const metrics = response.result.rows;
              if (!metrics) {
                return {};
              }

              metrics.map((m) => {
                const parsedDate = new Date(
                  `${m.dimensionValues[0].value.slice(0, 4)}-${m.dimensionValues[0].value.slice(
                    4,
                    6,
                  )}-${m.dimensionValues[0].value.slice(6)}`,
                );
                // The order aligns with the measurement order in the request
                recordDataForActiveUser.push({
                  name: alignUTC(format(parsedDate, 'yyyy-MM-dd HH:mm:ss')),
                  value: Number(m.metricValues[0].value),
                } as ChartEntry);

                recordDataForSessions.push({
                  name: alignUTC(format(parsedDate, 'yyyy-MM-dd HH:mm:ss')),
                  value: Number(m.metricValues[1].value),
                } as ChartEntry);
                recordDataForSessionDuration.push({
                  name: alignUTC(format(parsedDate, 'yyyy-MM-dd HH:mm:ss')),
                  value: Number(m.metricValues[2].value) * Number(m.metricValues[1].value),
                } as ChartEntry);
                recordDataForPageViews.push({
                  name: alignUTC(format(parsedDate, 'yyyy-MM-dd HH:mm:ss')),
                  value: Number(m.metricValues[3].value),
                } as ChartEntry);
                recordDataForPageViewsPerSession.push({
                  name: alignUTC(format(parsedDate, 'yyyy-MM-dd HH:mm:ss')),
                  value: Number(m.metricValues[4].value),
                } as ChartEntry);
                recordDataForNewUsers.push({
                  name: alignUTC(format(parsedDate, 'yyyy-MM-dd HH:mm:ss')),
                  value: Number(m.metricValues[5].value),
                } as ChartEntry);
                recordDataForBounceRate.push({
                  name: alignUTC(format(parsedDate, 'yyyy-MM-dd HH:mm:ss')),
                  value:
                    ((Number(m.metricValues[1].value) - Number(m.metricValues[7].value)) /
                      Number(m.metricValues[1].value)) *
                    100,
                  // value: m.metricValues[6].value
                } as ChartEntry);
                recordDataForEngSession.push({
                  name: alignUTC(format(parsedDate, 'yyyy-MM-dd HH:mm:ss')),
                  value: Number(m.metricValues[7].value),
                } as ChartEntry);
              });
              const data = {
                activeUsers: recordDataForActiveUser,
                sessions: recordDataForSessions,
                pageViews: recordDataForPageViews,
                sessionDuration: recordDataForSessionDuration,
                pageViewsPerSession: recordDataForPageViewsPerSession,
                newUsers: recordDataForNewUsers,
                bounceRate: recordDataForBounceRate,
                engagedSessions: recordDataForEngSession,
              } as Ga4RecordMap;
              observer.next(data);
              observer.complete();
            });
          });
      });
    }
  }

  private getBaseQuery(metricName: string, partnerId: string, businessId: string): QueryMetricsRequest {
    return new QueryMetricsRequest({
      partnerId: partnerId,
      metricName: metricName,
      resourceIds: [
        {
          businessId: { businessId: businessId },
        },
      ],
      filter: {
        compositeFilter: {
          op: CompositeFilter_Operator.AND,
          filters: [
            {
              fieldFilter: {
                dimension: 'category',
                operator: FieldFilter_Operator.EQUAL,
                value: {
                  valueType: Type.PROPERTY_TYPE_STRING,
                  value: Category[Category.CATEGORY_WEBSITE],
                },
              },
            },
            {
              fieldFilter: {
                dimension: 'subcategory',
                operator: FieldFilter_Operator.EQUAL,
                value: {
                  valueType: Type.PROPERTY_TYPE_STRING,
                  value: Category[Category.CATEGORY_WEBSITE],
                },
              },
            },
          ],
        },
      },
    });
  }

  private getGoalsRequest(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
  ): QueryMetricsRequest {
    const req: QueryMetricsRequest = this.getBaseQuery('google_analytics_ga4_goals', partnerId, businessId);
    req.measures = [
      new Measure({
        aggregate: new Aggregate({
          measure: 'completions',
          aggOp: Operator.SUM,
        }),
      }),
    ];
    req.groupBy = new GroupBy({
      dimension: [
        new Dimension({
          dimension: 'collection_timestamp',
        }),
        new Dimension({
          dimension: 'completions',
        }),
        new Dimension({
          dimension: 'goal_name',
        }),
      ],
    });
    req.alignmentPeriod = new AlignmentPeriod({
      calendar: Calendar.CALENDAR_YEAR,
    });
    req.alignment = Alignment.ALIGN_DELTA;
    req.dateRange = new DateRange({
      start: startDate,
      end: endDate,
    });
    req.orderBy = new OrderBy({
      orderBy: [{ column: 'collection_date', order: Order.ORDER_DESC }],
    });
    return req;
  }
  getSourcesBaseQuery(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    metricName: string,
    isDefault: boolean,
    siteId: string,
    accessToken: string,
  ): Observable<Ga4SourceMap> {
    if (isDefault === false) {
      const req: QueryMetricsRequest = this.getBaseQuery(metricName, partnerId, businessId);
      req.measures = [
        new Measure({
          aggregate: new Aggregate({
            measure: 'access_frequency',
            aggOp: Operator.SUM,
          }),
        }),
      ];
      req.groupBy = new GroupBy({
        dimension: [
          new Dimension({
            dimension: 'collection_timestamp',
          }),
          new Dimension({
            dimension: 'source',
          }),
        ],
      });
      req.alignmentPeriod = new AlignmentPeriod({
        calendar: Calendar.CALENDAR_YEAR,
      });

      req.alignment = Alignment.ALIGN_DELTA;
      req.dateRange = new DateRange({
        start: startDate,
        end: endDate,
      });
      return this.multiLocationAnalyticsService.queryMetrics(req).pipe(
        filter((resp) => resp != null),
        map((resp: QueryMetricsResponse) => {
          const metrics = resp.metricResults[0].metrics.metrics;
          if (!metrics) {
            return {};
          }
          return this.convertMetricsToMap(metrics);
        }),
      );
    } else {
      if (metricName === 'google_analytics_GA4_top_referral_sources') {
        return new Observable<Ga4SourceMap>((observer) => {
          gapi.client
            .init({
              discoveryDocs: ['https://analyticsdata.googleapis.com/$discovery/rest?version=v1beta'],
            })
            .then(() => {
              gapi.client.setToken({
                access_token: accessToken,
              });
              const respdata = this.buildQuery(
                this.getQueryOptions(this.topMetricsDefinitions, startDate, endDate, siteId, false),
              );
              respdata.subscribe((response) => {
                if (typeof response.result.rows === 'undefined') {
                  observer.next({});
                  observer.complete();
                  return {};
                }
                observer.next(this.convertMetricsToMapDefault(response.result.rows));
                observer.complete();
              });
            });
        });
      } else {
        return new Observable<Ga4SourceMap>((observer) => {
          gapi.client
            .init({
              discoveryDocs: ['https://analyticsdata.googleapis.com/$discovery/rest?version=v1beta'],
            })
            .then(() => {
              gapi.client.setToken({
                access_token: accessToken,
              });
              const respdata = this.buildQuery(
                this.getQueryOptions(this.treafficMetricsDefinitions, startDate, endDate, siteId, false),
              );
              respdata.subscribe((response) => {
                if (typeof response.result.rows === 'undefined') {
                  observer.next({});
                  observer.complete();
                  return {};
                }
                observer.next(this.convertMetricsToMapDefault(response.result.rows));
                observer.complete();
              });
            });
        });
      }
    }
  }

  convertMetricsToMap(metrics: MetricResult[]): Ga4SourceMap {
    const tableMap: Ga4SourceMap = {};
    metrics.forEach((m) => {
      const result = m.results.metrics[0];
      if (!tableMap[result.dimension]) {
        tableMap[result.dimension] = 0;
      }
      tableMap[result.dimension] += result.measures[0];
    });
    return tableMap;
  }

  convertMetricsToMapDefault(metrics: MetricResult[]): Ga4SourceMap {
    const tableMap: Ga4SourceMap = {};
    metrics.forEach((m) => {
      const result = m;
      if (!tableMap[result['dimensionValues'][0].value]) {
        tableMap[result['dimensionValues'][0].value] = 0;
      }
      tableMap[result['dimensionValues'][0].value] += Number(result['metricValues'][0].value);
    });
    return tableMap;
  }

  convertGoalMetricsToMap(metrics: MetricResult[]): Ga4GoalMap {
    const tableMap: Ga4GoalMap = {};
    metrics.forEach((m) => {
      const result = m.results.metrics[0];
      const goalId = result.results.metrics[0].dimension;
      const completions = result.results.metrics[0].measures[0];
      const goalName = result.results.metrics[0].dimension;
      if (!tableMap[goalId]) {
        tableMap[goalId] = {
          name: goalName,
          completions: completions,
        } as GaGoalData;
        return;
      }
      tableMap[goalId].completions += completions;
    });
    return tableMap;
  }

  convertMetricsResponseToMap(resp: QueryMetricsResponse): Ga4GoalMap {
    const metrics = resp.metricResults[0].metrics.metrics;
    if (!metrics) {
      return {};
    }
    return this.convertGoalMetricsToMap(metrics);
  }

  getTrafficSources(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    siteId: string,
    isDefault: boolean,
    accessToken: string,
  ): Observable<Ga4SourceMap> {
    return this.getSourcesBaseQuery(
      partnerId,
      businessId,
      startDate,
      endDate,
      'google_analyticsGA4_traffic_sources',
      isDefault,
      siteId,
      accessToken,
    );
  }

  getTopReferralSources(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
    siteId: string,
    isDefault: boolean,
    accessToken: string,
  ): Observable<Ga4SourceMap> {
    return this.getSourcesBaseQuery(
      partnerId,
      businessId,
      startDate,
      endDate,
      'google_analytics_GA4_top_referral_sources',
      isDefault,
      siteId,
      accessToken,
    );
  }

  getGoals(
    partnerId: string,
    businessId: string,
    startDate: Date,
    endDate: Date,
  ): Observable<Ga4GoalMap> {
    const req = this.getGoalsRequest(partnerId, businessId, startDate, endDate);
    return this.multiLocationAnalyticsService.queryMetrics(req).pipe(
      filter((resp) => resp != null),
      map((resp: QueryMetricsResponse) => this.convertMetricsResponseToMap(resp)),
    );
  }

  /**
   * Authenticates with the legacy Google Analytics connection by getting authentication
   * data from the backend
   * @param siteId
   */
  authenticate(siteId: string): Observable<AnalyticsJSON> {
    const url = `/api/v1/analytics/${siteId}/authenticate`;
    return this.http.get(url).pipe(map((res: { data: any }) => res.data));
  }

  /**
   * Constructs the query structure that is sent to Google Analytics for getting data about a specific GA view
   * @param params - return of getQueryOptions, which structured the query for the Google Analytics API
   */
  buildQuery(params: any): Observable<any> {
    const data = gapi.client.analyticsdata.properties.runReport(params);
    return from(data);
  }

  /**Default Tracking Request queries */

  /**
   * Constructs and returns the query that is used for receiving data from Google Analytics
   * @param metrics - the specific metrics to query Google Analytics for
   * @param dimension - the dimension to query
   * @param graphDefinition - the graph definition that contains the filters associated with the query that is
   * constructed
   *
   * For more information on what each parameter of the query does, see https://developers.google.com/analytics/devguides/reporting/core/v3/reference
   */
  getQueryOptions(
    graphDefinition: GA4AnalyticsGraphDefinition,
    start_date: Date,
    end_date: Date,
    siteId: string,
    isDimension: boolean,
  ): any {
    const query: any = {
      property: 'properties/' + this.legacyDefaultPropertyId,
      dateRanges: [
        {
          startDate: format(start_date, 'yyyy-MM-dd'),
          endDate: format(end_date, 'yyyy-MM-dd'),
        },
      ],
      metrics: graphDefinition.metrics,
      dimensionFilter: {
        filter: {
          fieldName: 'customEvent:dimension1',
          stringFilter: {
            value: siteId,
          },
        },
      },
    };
    if (!isDimension) {
      query.dimensions = graphDefinition.dimension;
    }
    return query;
  }
}
